import React from 'react';
import { Link } from 'react-router-dom';

interface DayCellProps {
  day: {
    day: number;
    date: string;
    pnl: number;
    followedPlan: boolean;
    hasData: boolean;
    planAdherenceRate?: number;
    tradeCount: number;
  } | null;
  mode: 'pnl' | 'plan';
  isMobile?: boolean;
}

export const DayCell: React.FC<DayCellProps> = ({ day, mode, isMobile = false }) => {
  if (!day) {
    return <div className={`${isMobile ? 'h-10' : 'h-full min-h-[110px]'} w-full bg-gray-50/50 dark:bg-gray-800/50 rounded-lg`}></div>;
  }
  
  const getBackgroundColor = () => {
    if (!day.hasData) return 'bg-white dark:bg-gray-800 hover:bg-gray-50/50 dark:hover:bg-gray-700/50';
    
    if (mode === 'pnl') {
      return day.pnl > 0 ? 'bg-emerald-300 hover:bg-emerald-400' : 
             day.pnl < 0 ? 'bg-red-300 hover:bg-red-400' : 
             'bg-amber-300 hover:bg-amber-400';
    } else {
      if (day.planAdherenceRate === undefined) return 'bg-white dark:bg-gray-800 hover:bg-gray-50/50 dark:hover:bg-gray-700/50';
      
      return day.planAdherenceRate >= 0.7 ? 'bg-emerald-300 hover:bg-emerald-400' :
             day.planAdherenceRate >= 0.5 ? 'bg-amber-300 hover:bg-amber-400' :
             'bg-red-300 hover:bg-red-400';
    }
  };

  // Helper to determine text color based on background
  const getTextColor = () => {
    if (!day.hasData) return 'text-gray-900 dark:text-gray-100';
    
    // For colored backgrounds (emerald, amber, red), always use dark text for readability
    if (mode === 'pnl') {
      return 'text-gray-900';
    } else {
      if (day.planAdherenceRate === undefined) return 'text-gray-900 dark:text-gray-100';
      return 'text-gray-900';
    }
  };

  const getPlanTextColor = () => {
    if (!day.hasData || day.planAdherenceRate === undefined) return 'text-gray-600 dark:text-gray-300';
    // For colored backgrounds, use darker text for better contrast
    return 'text-gray-700';
  };
  
  return (
    <Link 
      to={`/app/tradeLog?date=${day.date}`} 
      onClick={() => console.log('📅 DayCell: Clicked date:', day.date, '→ URL parameter')}
      className={`${isMobile ? 'h-10 p-1' : 'h-full min-h-[110px] p-3'} w-full rounded-lg transition-colors ${getBackgroundColor()} relative`}
    >
      <div className={`font-medium ${getTextColor()} ${isMobile ? 'text-center' : ''}`}>{day.day}</div>
      
      {day.hasData && !isMobile && (
        <div className="mt-2">
          {mode === 'pnl' && (
            <div className={`text-sm font-medium hidden sm:block ${day.pnl > 0 ? 'text-emerald-800' : day.pnl < 0 ? 'text-red-800' : 'text-amber-800'}`}>
              {day.pnl > 0 ? '+' : ''}{day.pnl.toFixed(2)}
            </div>
          )}
          
          {mode === 'plan' && day.planAdherenceRate !== undefined && (
            <div className={`text-sm hidden sm:block ${getPlanTextColor()}`}>
              {(day.planAdherenceRate * 100).toFixed(0)}% followed
            </div>
          )}
        </div>
      )}
      
      {day.tradeCount > 0 && (
        <div className={`absolute ${isMobile ? 'bottom-0 right-0.5 text-[10px]' : 'bottom-1 right-2 text-xs'} text-white font-medium opacity-80`}>
          {day.tradeCount}
        </div>
      )}
    </Link>
  );
};